import type { DisplayConfig, FrameRecords, SideDisplayConfig } from "../types/api";

// Resolve which columns to SHOW for a frame, mirroring the Streamlit statement tabs:
// lead with the curated display columns (in config order) that are present AND not
// entirely blank, then drop the rest. Zero data loss — filtering/search still runs
// over the full frame; this only controls which columns are DISPLAYED, decluttering
// the grid to the same concise set the app shows. `_sig` (the manual-match key) is
// never displayed but preserved for selection.
export interface VisibleColumn {
  key: string;      // raw column name in the frame
  label: string;    // friendly display name (from renames) or the raw name
  index: number;    // column index in frame.columns
}

// Blank-scan results per frame. Frames are replaced wholesale on re-finalize, so
// WeakMap keying is safe and the cache dies with the frame. Without this, every
// table re-render (page change, filter toggle, tab switch) re-scanned every column
// over every row — on a 96k-row Dubai frame that is ~4M string coercions per
// render. Computed once per (frame, column).
const BLANK_CACHE = new WeakMap<FrameRecords, Map<number, boolean>>();

function isBlankCell(v: unknown): boolean {
  // Fast paths first: null/undefined dominate sparse columns, and a non-empty
  // string or a number is non-blank without any coercion. Only the residue needs
  // String()+trim, which is what made the naive scan expensive.
  if (v === null || v === undefined) return true;
  const t = typeof v;
  if (t === "string") return (v as string).trim() === "";
  if (t === "number" || t === "boolean") return false;
  return String(v).trim() === "";
}

function columnAllBlank(frame: FrameRecords, index: number): boolean {
  let m = BLANK_CACHE.get(frame);
  if (!m) { m = new Map(); BLANK_CACHE.set(frame, m); }
  const hit = m.get(index);
  if (hit !== undefined) return hit;
  let blank = true;
  const rows = frame.rows;
  for (let i = 0; i < rows.length; i++) {
    if (!isBlankCell(rows[i][index])) { blank = false; break; }  // early exit
  }
  m.set(index, blank);
  return blank;
}

export function visibleColumns(frame: FrameRecords, config?: SideDisplayConfig): VisibleColumn[] {
  if (!config) {
    // No config: show every non-internal column (fallback).
    return frame.columns
      .filter((c) => !c.startsWith("_"))
      .map((c) => ({ key: c, label: c, index: frame.columns.indexOf(c) }));
  }
  const out: VisibleColumn[] = [];
  for (const key of config.columns) {
    const index = frame.columns.indexOf(key);
    if (index < 0) continue;                  // column not present in this frame
    if (columnAllBlank(frame, index)) continue; // entirely empty → suppressed (like the app)
    out.push({ key, label: config.renames[key] ?? key, index });
  }
  return out;
}

// Engine columns that ride along in the frames for client-side slicing but are never
// shown as table columns (match_status IS shown — it's in the curated display_config
// order as "Match Status").
// recon_category rides along for the category filter and the Statement tab's
// sectioned grouping, but is never a column: finance doesn't use it and it
// misclassifies on several live insurers. Dropping it from display_config alone
// would resurface it here as a title-cased "Recon Category" extra.
// matched_row_id/match_group_id ride along purely as the Side-by-Side pairing key —
// they are matcher bookkeeping, meaningless to finance as a grid column.
// in_period_window feeds the Other tab's in/out-of-window split; as a column it
// rendered a bare "true"/"false". The Excel statements already drop it for the same
// reason (reporting/export.py::_STATEMENT_DROP). is_settled_paid likewise feeds only
// the Other tab's Settled section.
// period_position / movement are deliberately NOT hidden: they are the finance-facing
// display twins of in_period_window and the amount column (see backend/summary.py).
// `movement` is the figure that sums to the closing — the Excel Statement sheets have
// carried it for exactly that reason, and it now appears on every table too.
const HIDDEN_EXTRA_COLS = new Set([
  "role", "signed_amount", "matched_row_id", "match_group_id", "in_period_window",
  "recon_category", "is_settled_paid",
]);

// Friendly labels for the two engine-derived display columns, applied wherever a
// frame is rendered without a curated config entry for them.
export const DERIVED_COL_LABELS: Record<string, string> = {
  period_position: "Period",
  movement: "Movement (Debit - Credit)",
  // Normally supplied by the server's display_config; named here too so a frame rendered
  // without one never reads "Match Group Label".
  match_group_label: "Match Group",
};

// The `period_position` values, mirroring backend/summary.py's PERIOD_* constants.
// These strings ARE the filter contract on both surfaces — changing one here without
// changing it there silently empties the default view.
export const PERIOD_IN = "In period";
export const PERIOD_BEFORE = "Before period";
export const PERIOD_AFTER = "After period";

export function titleCase(name: string): string {
  return name.replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());
}

// FULL column set for a frame slice, mirroring the Streamlit transaction tables
// (app/tables.py::_render_transaction_table): the curated display columns first (in
// config order, friendly names), then every OTHER populated column title-cased —
// engine-internal / underscore-prefixed columns suppressed, all-blank columns dropped.
// `rowPositions` limits the populated-check to a slice (e.g. one drill-down bucket).
/**
 * Of `names`, the ones ANY row of `frame` populates.
 *
 * For a caller that must decide a column set ONCE over a whole population rather than
 * per rendered slice — a column blank in the slice on screen but filled on another row
 * is still one of the file's columns, and pruning it per slice makes the same table
 * show different columns depending on which view you are standing in.
 */
export function populatedColumns(frame: FrameRecords, names: string[]): string[] {
  return names.filter((n) => {
    const i = frame.columns.indexOf(n);
    return i >= 0 && !columnAllBlank(frame, i);
  });
}

/**
 * `keepCurated` renders every curated column whether or not it is blank in this slice.
 * For a grid whose column set was already decided over a wider population (see
 * populatedColumns) — without it, allColumns would prune it a second time against the
 * rows on screen and undo that decision.
 */
export function allColumns(frame: FrameRecords, config?: SideDisplayConfig,
                           rowPositions?: number[],
                           keepCurated = false): VisibleColumn[] {
  const blank = (index: number): boolean => {
    if (!rowPositions) return columnAllBlank(frame, index);
    for (const p of rowPositions) {
      if (!isBlankCell(frame.rows[p]?.[index])) return false;
    }
    return true;
  };
  const out: VisibleColumn[] = [];
  const seen = new Set<string>();
  for (const key of config?.columns ?? []) {
    const index = frame.columns.indexOf(key);
    if (index < 0) continue;
    if (!keepCurated && blank(index)) continue;
    out.push({ key, label: config?.renames[key] ?? key, index });
    seen.add(key);
  }
  frame.columns.forEach((key, index) => {
    if (seen.has(key) || key.startsWith("_") || HIDDEN_EXTRA_COLS.has(key)) return;
    if (config?.columns.includes(key)) return; // curated but blank in this slice → stays suppressed
    if (blank(index)) return;
    out.push({ key, label: DERIVED_COL_LABELS[key] ?? titleCase(key), index });
  });
  return out;
}

// Reorder resolved columns so pinned ones lead, in PIN order (the order the user
// pinned them), with every other column keeping its existing relative order. Applied
// as the last step of column resolution so pinning composes with the curated-first
// rule rather than replacing it. A pin naming a column absent from this frame (e.g.
// a pin set on another insurer's SOA, or a column blank in this slice) is simply
// inert — no error, no empty column.
export function applyPins(cols: VisibleColumn[], pinned: string[]): VisibleColumn[] {
  if (!pinned.length || !cols.length) return cols;
  const rank = new Map(pinned.map((k, i) => [k, i]));
  const lead: VisibleColumn[] = [];
  const rest: VisibleColumn[] = [];
  for (const c of cols) (rank.has(c.key) ? lead : rest).push(c);
  if (!lead.length) return cols;
  lead.sort((a, b) => (rank.get(a.key) as number) - (rank.get(b.key) as number));
  return [...lead, ...rest];
}

// Original-frame-index → row position lookup, cached per frame object (frames are
// replaced wholesale on re-finalize, so WeakMap keying is safe). Duplicate indices
// should not occur (notes keying already relies on uniqueness) — first position wins.
const INDEX_MAP_CACHE = new WeakMap<FrameRecords, Map<string, number>>();

export function frameIndexMap(frame: FrameRecords): Map<string, number> {
  let m = INDEX_MAP_CACHE.get(frame);
  if (!m) {
    const built = new Map<string, number>();
    frame.indices.forEach((idx, pos) => {
      const k = String(idx);
      if (!built.has(k)) built.set(k, pos);
    });
    INDEX_MAP_CACHE.set(frame, built);
    m = built;
  }
  return m;
}

// `_sig` → row position lookup, cached per frame object (WeakMap, safe because frames
// are replaced wholesale on re-finalize). First position wins. Lets a caller holding a
// signature slice allColumns() to that single row for a full-detail view.
const SIG_POS_CACHE = new WeakMap<FrameRecords, Map<string, number>>();

export function sigPosMap(frame: FrameRecords): Map<string, number> {
  let m = SIG_POS_CACHE.get(frame);
  if (!m) {
    const si = frame.columns.indexOf("_sig");
    const built = new Map<string, number>();
    if (si >= 0) {
      frame.rows.forEach((r, pos) => {
        const k = String(r[si] ?? "");
        if (k && !built.has(k)) built.set(k, pos);
      });
    }
    SIG_POS_CACHE.set(frame, built);
    m = built;
  }
  return m;
}

// Money columns by canonical key: the curated "(AED)" columns of both books
// (reporting/filters.py::_get_display_config), the CRI export's own amount fields and the
// derived movement. By key, never by "holds a number": an ID column can hold numbers too.
export const MONEY_KEYS = new Set([
  "cri_amount", "amount", "allocated_amount", "balance", "debits", "credits",
  "debit", "credit", "balance_amount", "original_amount", "movement",
  // Customer roll-forward (step 1) figures.
  "prev_close", "curr_open", "variance", "curr_close",
]);

// The insurer's own amount columns (FC_AMT, 'AR Balance In AED'), as the server names them
// in display_config.<side>.money_columns: recognised there by their values, since a raw
// header says nothing reliable. Set by ResultPage as it renders, so before any grid or
// search index below it formats a cell; a new result brings new frames, so no per-frame
// cache holds the old set.
let rawMoneyColumns = new Set<string>();

export function setRawMoneyColumns(config?: DisplayConfig): void {
  rawMoneyColumns = new Set([...(config?.cri?.money_columns ?? []),
                             ...(config?.insurer?.money_columns ?? [])]);
}

export function isMoneyKey(colKey: string): boolean {
  // mv_<bucket>: the roll-forward's movement-by-voucher-type columns.
  return MONEY_KEYS.has(colKey) || colKey.startsWith("mv_") || rawMoneyColumns.has(colKey);
}

// An amount as finance reads it: thousands separators and at least two decimals, printed
// from the value rounded to 6 decimals with trailing zeros trimmed. That drops binary float
// noise the source files carry (683.3100000000001, an SOA formula's -1000.179999999993)
// yet never a real figure: 0.125 stays 0.125, Hayah's pro-rata -221.41282191780823 shows
// -221.412822, and no shown amount is off by more than 0.0000005. Display-only. [shown,
// plain] — 2,244.90 and 2244.90 — or null for NaN/Infinity or 1e21+ (toFixed's exponent
// range), which is then shown as-is. Hand-rolled, not a regex: the search index runs it on
// every amount cell of the book (223k on Dubai's insurer side).
function moneyParts(n: number): [string, string] | null {
  if (!Number.isFinite(n) || Math.abs(n) >= 1e21) return null;
  const s = n.toFixed(6);
  const neg = s.charCodeAt(0) === 45;
  const dot = s.length - 7;
  let end = s.length;
  while (end > dot + 3 && s.charCodeAt(end - 1) === 48) end--;
  const int = s.slice(neg ? 1 : 0, dot);
  const frac = s.slice(dot + 1, end);
  let grouped = int;
  if (int.length > 3) {
    const head = int.length % 3 || 3;
    grouped = int.slice(0, head);
    for (let i = head; i < int.length; i += 3) grouped += "," + int.slice(i, i + 3);
  }
  // Noise around zero (-1e-9) rounds to -0.000000: a zero carries no sign.
  const sign = neg && (int !== "0" || frac !== "00") ? "-" : "";
  return [`${sign}${grouped}.${frac}`, `${sign}${int}.${frac}`];
}

export function fmtMoney(n: number): string {
  return moneyParts(n)?.[0] ?? String(n);
}

// The searchable forms of an amount: as stored (2244.9), as the ledgers show it
// (2,244.90) and as typed from a spreadsheet (2244.90), so what is on screen is findable.
// Separate, so a whole-cell match can equal any one of them.
export function moneyForms(n: number): string[] {
  const s = String(n);
  const parts = moneyParts(n);
  if (!parts) return [s];
  const out = [s];
  if (parts[0] !== s) out.push(parts[0]);
  if (parts[1] !== s && parts[1] !== parts[0]) out.push(parts[1]);
  return out;
}

// The same, as one string for a substring-only search index. A stored form of at most 6
// decimals is a prefix of the plain one (only zeros are appended), so leaving it out finds
// the same needles for less work; past 6 decimals the stored digits are noise nobody types.
export function moneySearchText(n: number): string {
  const parts = moneyParts(n);
  if (!parts) return String(n);
  return parts[0] === parts[1] ? parts[1] : parts[0] + " " + parts[1];
}

// Display formatting for a table cell, mirroring the Streamlit tables: blanks/NaN
// as an em-dash, ISO dates in date-named columns as dd/mm/yyyy. Display-only —
// never changes the underlying value used for filtering/keys.
export function fmtCell(value: unknown, colKey: string): string {
  if (value === null || value === undefined) return "—";
  const s = String(value).trim();
  if (s === "" || s === "nan" || s === "NaT" || s === "None") return "—";
  if (colKey.toLowerCase().includes("date")) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  }
  return s;
}

/** A grid cell as every table shows it: an amount grouped (fmtMoney, lossless), anything
 *  else as fmtCell. Pass the RAW key (rawKeyOf) for a paired grid's composite column. */
export function cellText(value: unknown, colKey: string): string {
  return typeof value === "number" && isMoneyKey(colKey) ? fmtMoney(value) : fmtCell(value, colKey);
}
